import { supabase } from '../lib/supabase';

export const fatouMonitoringService = {
  /**
   * Get conversation logs with filtering and pagination
   */
  async getConversationLogs(filters = {}) {
    try {
      let query = supabase?.from('fatou_conversation_logs')?.select('*')?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }
      if (filters?.isFlagged !== undefined && filters?.isFlagged !== null) {
        query = query?.eq('is_flagged', filters?.isFlagged);
      }
      if (filters?.topic) {
        query = query?.eq('conversation_topic', filters?.topic);
      }
      if (filters?.minConfidence !== undefined) {
        query = query?.gte('confidence_score', filters?.minConfidence);
      }
      if (filters?.maxConfidence !== undefined) {
        query = query?.lte('confidence_score', filters?.maxConfidence);
      }
      if (filters?.searchQuery) {
        query = query?.or(`user_message.ilike.%${filters?.searchQuery}%,ai_response.ilike.%${filters?.searchQuery}%`);
      }

      // Pagination
      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }
      if (filters?.offset) {
        query = query?.range(filters?.offset, filters?.offset + (filters?.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching conversation logs:', error);
      throw error;
    }
  },

  /**
   * Get conversation analytics
   */
  async getConversationAnalytics(startDate, endDate) {
    try {
      let query = supabase?.from('fatou_conversation_analytics')?.select('*')?.order('date', { ascending: false });

      if (startDate) {
        query = query?.gte('date', startDate);
      }
      if (endDate) {
        query = query?.lte('date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching conversation analytics:', error);
      throw error;
    }
  },

  /**
   * Get real-time conversation statistics
   */
  async getConversationStats() {
    try {
      const { data, error } = await supabase?.rpc('get_fatou_conversation_stats');
      
      if (error) {
        // If RPC doesn't exist, calculate manually
        const { data: logs, error: logsError } = await supabase?.from('fatou_conversation_logs')?.select('*');

        if (logsError) throw logsError;

        const stats = {
          totalConversations: new Set(logs?.map(l => l.session_id))?.size,
          totalMessages: logs?.length || 0,
          flaggedCount: logs?.filter(l => l?.is_flagged)?.length || 0,
          avgConfidence: logs?.length > 0 
            ? (logs?.reduce((sum, l) => sum + (parseFloat(l?.confidence_score) || 0), 0) / logs?.length)?.toFixed(2)
            : 0,
          todayCount: logs?.filter(l => {
            const logDate = new Date(l.created_at);
            const today = new Date();
            return logDate?.toDateString() === today?.toDateString();
          })?.length || 0
        };

        return stats;
      }

      return data;
    } catch (error) {
      console.error('Error fetching conversation stats:', error);
      throw error;
    }
  },

  /**
   * Flag a conversation as incorrect
   */
  async flagConversation(logId, flagData) {
    try {
      // Update conversation log
      const { error: updateError } = await supabase?.from('fatou_conversation_logs')?.update({
          is_flagged: true,
          flag_reason: flagData?.reason,
          flagged_at: new Date()?.toISOString()
        })?.eq('id', logId);

      if (updateError) throw updateError;

      // Create flag record
      const { data, error } = await supabase?.from('fatou_response_flags')?.insert({
          conversation_log_id: logId,
          flag_type: flagData?.type,
          severity: flagData?.severity,
          description: flagData?.description,
          suggested_correction: flagData?.suggestedCorrection
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error flagging conversation:', error);
      throw error;
    }
  },

  /**
   * Unflag a conversation
   */
  async unflagConversation(logId) {
    try {
      const { error } = await supabase?.from('fatou_conversation_logs')?.update({
          is_flagged: false,
          flag_reason: null,
          flagged_at: null
        })?.eq('id', logId);

      if (error) throw error;

      // Delete associated flags
      await supabase?.from('fatou_response_flags')?.delete()?.eq('conversation_log_id', logId);

      return true;
    } catch (error) {
      console.error('Error unflagging conversation:', error);
      throw error;
    }
  },

  /**
   * Get response flags with filtering
   */
  async getResponseFlags(filters = {}) {
    try {
      let query = supabase?.from('fatou_response_flags')?.select(`
          *,
          conversation_log:conversation_log_id (
            user_message,
            ai_response,
            created_at
          )
        `)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }
      if (filters?.flagType) {
        query = query?.eq('flag_type', filters?.flagType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching response flags:', error);
      throw error;
    }
  },

  /**
   * Update flag status
   */
  async updateFlagStatus(flagId, status, reviewNotes) {
    try {
      const { data, error } = await supabase?.from('fatou_response_flags')?.update({
          status,
          review_notes: reviewNotes,
          reviewed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', flagId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating flag status:', error);
      throw error;
    }
  },

  /**
   * Get unique conversation topics
   */
  async getConversationTopics() {
    try {
      const { data, error } = await supabase?.from('fatou_conversation_logs')?.select('conversation_topic')?.not('conversation_topic', 'is', null);

      if (error) throw error;

      const topics = [...new Set(data?.map(d => d.conversation_topic))]?.filter(Boolean);
      return topics;
    } catch (error) {
      console.error('Error fetching conversation topics:', error);
      return [];
    }
  },

  /**
   * Log a conversation (called from edge function or client)
   */
  async logConversation(conversationData) {
    try {
      const { data, error } = await supabase?.from('fatou_conversation_logs')?.insert({
          session_id: conversationData?.sessionId,
          user_message: conversationData?.userMessage,
          ai_response: conversationData?.aiResponse,
          confidence_score: conversationData?.confidenceScore || 0.75,
          conversation_topic: conversationData?.topic,
          response_time_ms: conversationData?.responseTime
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error logging conversation:', error);
      throw error;
    }
  }
};

export default fatouMonitoringService;